'use client';

import { FC } from 'react';
import { css } from '@bts-web/utils-style-engine';
import {
  SavingsPlanTransactionItemType,
  StakingTransactionItemType,
} from '@bts-web/core-features/transactions';
import { getTransactionDetailsFieldsData } from '../utils/getTransactionDetailsFieldsData';
import {
  TransactionDetailsField,
  TransactionDetailsFieldVariants,
} from '../TransactionDetailsField/TransactionDetailsField';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { useFeatureFlag } from '../../statsig/sdk/provider';
import { TFunction } from 'i18next';
import { TransactionDetailsTransactionKeysResult } from '../utils';
import {
  CryptoTransfersTransactionsItemType,
  TransactionViewQueryData,
  SwapOfferViewQueryData,
  CryptoActionViewQueryData,
  FiatTransactionViewQueryData,
} from '../types';

const fieldsContainerStyles = css({
  marginBottom: 'extra_small',
  paddingX: 'medium',
  width: '100%',
  flex: '1',
  flexDirection: 'column',
  display: 'flex',
  overflow: 'auto',
});

export type TransactionDetailsWrapperProps = {
  transaction:
    | TransactionViewQueryData
    | CryptoTransfersTransactionsItemType
    | SavingsPlanTransactionItemType
    | StakingTransactionItemType
    | SwapOfferViewQueryData
    | CryptoActionViewQueryData
    | FiatTransactionViewQueryData;
  translations: TransactionDetailsTransactionKeysResult;
  locale: string;
  currency?: string;
};

export const TransactionDetailsWrapper: FC<TransactionDetailsWrapperProps> = ({
  transaction,
  translations,
  locale,
  currency,
}) => {
  const { t } = useClientTranslation();

  const feature_showAssetClassification = useFeatureFlag(
    'feature_showAssetClassification',
  );

  const feature_showTaxWitheldForCryptoTransactions = useFeatureFlag(
    'feature_showTaxWitheldForCryptoTransactions',
  );

  const transactionDetailFields = getTransactionDetailsFieldsData({
    translations,
    transaction,
    locale,
    currency,
    translationUtil: t as TFunction,
    flags: {
      feature_showAssetClassification,
      feature_showTaxWitheldForCryptoTransactions,
    },
  });

  return (
    <ul
      className={fieldsContainerStyles}
      role="listbox"
      data-testid="transaction-details-wrapper"
    >
      {transactionDetailFields.map((field) => {
        switch (field.kind) {
          case 'date-only':
            return (
              <TransactionDetailsField
                key={field.name}
                data-testid={field.name}
                locale={locale}
                label={field.label}
                value={field.value}
                variant={TransactionDetailsFieldVariants.TRANSACTION_DATE}
              />
            );

          case 'date-hour':
            return (
              <TransactionDetailsField
                key={field.name}
                data-testid={field.name}
                locale={locale}
                label={field.label}
                value={field.value}
                variant={TransactionDetailsFieldVariants.TRANSACTION_DATE_HOUR}
              />
            );

          case 'status':
            return (
              <TransactionDetailsField
                key={field.name}
                data-testid={field.name}
                locale={locale}
                label={field.label}
                value={field.value}
                transactionType={field.transactionType}
                status={field.status}
                variant={field.variant}
              />
            );

          case 'wallet-address':
            return (
              <TransactionDetailsField
                key={field.name}
                data-testid={field.name}
                label={field.label}
                locale={locale}
                value={field.value}
                variant={TransactionDetailsFieldVariants.WALLET_ADDRESS}
              />
            );

          case 'text':
            return (
              <TransactionDetailsField
                key={field.name}
                data-testid={field.name}
                label={field.label}
                locale={locale}
                value={field.value}
              />
            );
        }
      })}
    </ul>
  );
};
